import { useState, useEffect, useRef } from 'react';
import { useLang } from '../context/LanguageContext';
import { useVisionA11y } from '../context/VisionAccessibilityContext';
import { useContent } from '../hooks/useContent';
import './Navbar.css';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const { lang, setLang } = useLang();
  const { NAV_ITEMS, ui } = useContent();
  const { enabled: a11yEnabled, setEnabled: setA11yEnabled } = useVisionA11y();
  const closeTimerRef = useRef(null);

  const openDropdown = idx => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpenDrop(idx);
  };

  const closeDropdownWithDelay = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setOpenDrop(null);
      closeTimerRef.current = null;
    }, 180);
  };

  const isExternalLink = href => typeof href === 'string' && /^https?:\/\//.test(href);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  return (
    <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
      <a href="#hero" className="nav-brand">
        <img
          src={lang === 'ҚАЗ' ? '/logo-kz.png' : 'https://fincenter.kz/assets/logo%20ru-f4bf5168.svg'}
          alt={ui.brand.alt}
          className="nav-logo"
          onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
        />
        <span className="nav-logo-fallback" style={{display:'none'}}>
          <svg width="36" height="42" viewBox="0 0 80 92" fill="none">
            <path d="M8 80 L38 4 L52 4 L22 80 Z" fill="white" opacity="0.95"/>
            <path d="M26 80 L56 4 L64 4 L34 80 Z" fill="white" opacity="0.7"/>
            <path d="M44 80 L58 40 L66 40 L52 80 Z" fill="white" opacity="0.5"/>
            <path d="M44 80 L58 60 L72 80 Z" fill="#00a6ca"/>
          </svg>
          <span className="nav-brand-text">
            <span className="nav-brand-name">{ui.brand.name}</span>
            <span className="nav-brand-descriptor">{ui.brand.descriptor}</span>
          </span>
        </span>
      </a>

      <ul className="nav-links">
        {NAV_ITEMS.map((item, i) => (
          <li
            key={i}
            className={`nav-item ${item.submenu ? 'nav-item--drop' : ''}`}
            onMouseEnter={() => item.submenu && openDropdown(i)}
            onMouseLeave={() => item.submenu && closeDropdownWithDelay()}
          >
            <a href={item.href} className="nav-link">
              {item.label}
              {item.submenu && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
              )}
            </a>
            {item.submenu && openDrop === i && (
              <div className="nav-dropdown">
                {item.submenu.map((sub, j) => (
                  <a
                    key={j}
                    href={sub.href}
                    className="nav-dropdown-item"
                    target={isExternalLink(sub.href) ? '_blank' : undefined}
                    rel={isExternalLink(sub.href) ? 'noreferrer' : undefined}
                  >
                    {sub.label}
                  </a>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>

      <div className="nav-actions">
        <button
          type="button"
          className={`nav-a11y-toggle ${a11yEnabled ? 'active' : ''}`}
          onClick={() => setA11yEnabled(!a11yEnabled)}
          aria-pressed={a11yEnabled}
          title={ui.a11y.visionToggle}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span className="nav-a11y-label">Аа</span>
        </button>
        <div className="nav-lang">
          {['ҚАЗ','РУС',].map(l => (
            <button key={l} className={`lang-btn ${lang===l?'active':''}`} onClick={() => setLang(l)}>{l}</button>
          ))}
        </div>
        
        <button className="nav-burger" aria-label={ui.common.menu} onClick={() => setMobileOpen(!mobileOpen)}>
          <span/><span/><span/>
        </button>
      </div>

      {/* MOBILE MENU */}
      {mobileOpen && (
        <div className="nav-mobile">
          {NAV_ITEMS.map((item, i) => (
            <div key={i}>
              <a href={item.href} className="nav-mobile-link" onClick={() => setMobileOpen(false)}>{item.label}</a>
              {item.submenu && item.submenu.map((sub, j) => (
                <a
                  key={j}
                  href={sub.href}
                  className="nav-mobile-sub"
                  target={isExternalLink(sub.href) ? '_blank' : undefined}
                  rel={isExternalLink(sub.href) ? 'noreferrer' : undefined}
                  onClick={() => setMobileOpen(false)}
                >
                  {sub.label}
                </a>
              ))}
            </div>
          ))}
          <div className="nav-mobile-bottom">
            <button
              type="button"
              className={`nav-a11y-toggle nav-a11y-toggle--mobile ${a11yEnabled ? 'active' : ''}`}
              onClick={() => { setA11yEnabled(!a11yEnabled); setMobileOpen(false); }}
              aria-pressed={a11yEnabled}
            >
              {ui.a11y.visionToggle}
            </button>
            <div className="nav-lang">
              {['ҚАЗ','РУС'].map(l => (
                <button key={l} className={`lang-btn ${lang===l?'active':''}`} onClick={() => setLang(l)}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}